<div class="relative flex flex-col w-full space-y-1 h-11 max-h-11">
  <!-- The input element itself -->
  <div
    #selectInputElement
    class="flex flex-col items-stretch justify-center h-full cursor-pointer"
    [ngClass]="{
      'rounded-[100px]': isRound,
      'bg-neutral-900 shadow': !isTransparent,
      'bg-transparent' : isTransparent,
      'border-b-1 border-b-gray-700 rounded-t': !isRound && !isTransparent,
      'opacity-50 select-none pointer-events-none': disabled
    }"
    tabindex="0"
    (click)="toggleShowOptions()"
    (keyup)="null"
  >
    @if (label) {
      <span
        class="ml-4 mt-0.5 text-xxs"
        [ngClass]="{
          'text-error' : formControl.touched && formControl.invalid,
          'text-cyan': showOptions() && !(formControl.touched && formControl.invalid),
          'text-gray-400': !showOptions && !(formControl.touched && formControl.invalid)
        }"
      >{{ label }}</span>
    }

    <div
      class="flex items-center justify-between"
      [ngClass]="{
        'pb-1.5': !isRound,
        'px-4': !fitContent,
      }"
    >
      <input
        #inputElement
        type="text"
        class="w-1 h-5 p-0 text-sm bg-transparent border-transparent outline-none focus:border-transparent focus:outline-none placeholder:text-white focus:ring-0"
        [ngClass]="{
          'w-1': fitContent && !(selectedOptions | toArray | isNonEmptyArray),
          'w-full': !fitContent || (selectedOptions | toArray | isNonEmptyArray),
          'border-b-error': searchInputFormControl.errors?.['pattern'],
        }"
        [formControl]="searchInputFormControl"
        [name]="name + '-search-input'"
        [id]="id + '-search-input'"
        [autocomplete]="name + '-search-input'"
        [placeholder]="(selectedOptions | toArray | isNonEmptyArray) && !multiple ? ((selectedOptions | toArray)[0] | extractProperty: labelProperty) : placeholder"
        [disabled]="disabled || false"
      />

      <nrc-icon
        class="w-4 h-4"
        [ngClass]="{
          'pb-2': !isRound && !isTransparent,
          'pb-1': isTransparent
        }"
        [name]="'select'"
        [classList]="showOptions() ? ['transition-transform', 'rotate-180'] : ['transition-transform']"
      />
    </div>
  </div>

  <!-- The collapsible options menu of the multi select -->
  @if (showOptions() || (searchInputFormControl.value && searchInputFormControl.value.length > 0)) {
    <div
      [nrcOverlayPortal]="selectInputElement"
      [overflowParent]="overflowParentElement"
      [offset]="isRound ? 24 : 4"
      [matchWidth]="true"
      class="z-10 w-full overflow-auto rounded-md min-w-48 max-h-48 bg-neutral-900"
      [ngClass]="{
        'flex flex-col justify-center': options.length === 0
      }"
      [@growAnimation]="{
        value: '', params: {
          heightStart: '0',
          heightEnd: '80px'
        }
      }"
    >
      <ng-container [ngTemplateOutlet]="filteredOptionsList" />
    </div>
  }
</div>

<!-- The list of selected options represented as chips -->
@if (multiple && showTags) {
  <div class="flex flex-col gap-2">
    <div class="flex flex-wrap mt-3">
      @for (selectedOption of (selectedOptions | toArray).slice(0, 6); track i; let i = $index) {
        <nrc-chip
          class="mb-2 mr-2"
          @fadeInOutAnimation
          [label]="(selectedOption | extractProperty: labelProperty) | toString"
          [config]="{ isClosable: true }"
          (closeEvent)="removeOption(selectedOption)"
        />
      }
    </div>

    @if ((selectedOptions | toArray).length > 6) {
      <span class="text-sm text-white/70">
        +{{ (selectedOptions | toArray).length - 6 }} more...
      </span>
    }
  </div>
}

<ng-template #filteredOptionsList class="relative flex flex-col w-full">
  @if (!optionsFiltered || optionsFiltered.length === 0) {
    @if (showAddOption() && allowAddingOptions && searchInputFormControl.value) {
      <div
        class="flex flex-col w-full overflow-auto rounded-md min-w-48 max-h-48 bg-neutral-900 top-10"
        tabindex="0"
        (click)="addNewOption()"
        (keyup)="null"
      >
        <div class="flex items-center transition-[color] duration-250 ease-in-out px-4 py-2 cursor-pointer hover:bg-neutral-800">
          <nrc-icon
            class="w-3 h-3 mr-2"
            name="plus"
          />

          <div class="flex flex-row items-center justify-center gap-2">
            <span class="text-sm text-cyan"> Add </span>
            <span class="text-xs/[14px] text-[#f8f8f8]"> "{{ searchInputFormControl.value }}" </span>
          </div>
        </div>
      </div>
    }

    <span class="flex p-4 text-sm cursor-pointer transition-all duration-250 ease-in-out">
      No results found
    </span>
  }
  @if (optionsFiltered | isNonEmptyArray) {
    @for (option of optionsFiltered; track i; let i = $index) {
      <div
        class="group relative flex items-center text-xs/[14px] text-[#f8f8f8] transition-[color] duration-250 ease-in-out px-4 py-2 cursor-pointer hover:bg-white/5"
        tabindex="0"
        (click)="selectOption(option)"
        (keyup)="null"
      >
        @if (multiple) {
          <span
            class="flex justify-center items-center mr-4 w-5 h-5 bg-transparent
              border-solid rounded-sm cursor-pointer
              transition-all duration-250 ease-in-out
              group-[hover]:bg-transparent group-[]:bg-transparent"
            [ngClass]="{
              'border-1 border-gray-400': !(option | hasSetValue:selectedOptions),
              'border-10 border-cyan animate-shrink-bounce before:content-[\'\'] before:absolute before:top-5 before:left-5 before:border-r-1.5 before:border-r-solid before:border-r-transparent before:border-b-1.5 before:border-b-solid before:border-b-transparent before:rotate-45 before:origin-bottom-left before:animate-checkbox-check': option | hasSetValue:selectedOptions
            }"
          ></span>
        }

        <span class="cursor-pointer">
          {{ option | extractProperty: labelProperty }}
        </span>
      </div>
    }
  }
</ng-template>
